/**
 * Delete Client Dialog (Story 3.9, AC #1, #6)
 *
 * Type-to-confirm deletion dialog that permanently removes a client
 * and all associated data (integrations, reports, tag assignments).
 * Uses Framer Motion animations matching AddClientSheet.
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button, Input } from "@provenleap/ui";
import { Trash } from "@phosphor-icons/react";
import { apiDelete } from "../../lib/apiClient";
import type { ToastType } from "../../hooks/useToast";

interface DeleteClientDialogProps {
  client: { id: string; name: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
  showToast: (type: ToastType, message: string) => void;
}

export function DeleteClientDialog({
  client,
  open,
  onOpenChange,
  onDeleted,
  showToast,
}: DeleteClientDialogProps) {
  const [confirmText, setConfirmText] = useState("");
  const queryClient = useQueryClient();
  const isNameMatch = client ? confirmText === client.name : false;

  // Reset typed name when dialog closes
  useEffect(() => {
    if (!open) setConfirmText("");
  }, [open]);

  const deleteClient = useMutation({
    mutationFn: async () => {
      if (!client) throw new Error("No client selected");
      const res = await apiDelete(`/api/clients/${client.id}`);
      if (!res.ok) {
        const json = await res.json().catch(() => ({ detail: "Server error" }));
        throw { status: res.status, ...json };
      }
    },
    onSuccess: () => {
      if (client) {
        queryClient.invalidateQueries({ queryKey: ["clients"] });
        queryClient.invalidateQueries({ queryKey: ["clients", client.id] });
      }
      showToast("success", "Client deleted");
      onDeleted();
    },
    onError: (err: any) => {
      if (err?.status === 404) {
        showToast("info", "Client not found — it may have already been deleted");
        onDeleted();
      } else {
        showToast(
          "error",
          err?.title ?? err?.detail ?? "Failed to delete client"
        );
      }
    },
  });

  if (!client) return null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            {/* Overlay */}
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            {/* Content */}
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100vw-2rem)] sm:max-w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-3 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Permanently delete {client.name}?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    This will permanently delete this client and all associated data.
                    This action cannot be undone.
                  </DialogPrimitive.Description>
                </div>

                <div className="space-y-3 py-4">
                  <motion.p
                    className="text-sm text-muted-foreground"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    The following will be deleted:
                  </motion.p>
                  <motion.ul
                    className="list-disc pl-5 text-sm text-muted-foreground space-y-1"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <li>Client profile and logo</li>
                    <li>All integration connections</li>
                    <li>All generated reports</li>
                    <li>All tag assignments (tags themselves are preserved)</li>
                  </motion.ul>

                  <motion.div
                    className="pt-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <label
                      htmlFor="delete-confirm-input"
                      className="text-sm font-medium"
                    >
                      Type <span className="font-semibold text-destructive">{client.name}</span> to
                      confirm
                    </label>
                    <Input
                      id="delete-confirm-input"
                      placeholder="Type client name to confirm"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      className="mt-1.5 rounded-lg"
                      data-testid="delete-confirm-input"
                      autoComplete="off"
                    />
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-2">
                    <Button
                      variant="ghost"
                      className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                      style={{ border: "0.5px solid hsl(var(--input))" }}
                      onClick={() => onOpenChange(false)}
                      disabled={deleteClient.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      className="rounded-xl cursor-pointer"
                      onClick={() => deleteClient.mutate()}
                      disabled={!isNameMatch || deleteClient.isPending}
                      data-testid="confirm-delete-btn"
                    >
                      <Trash size={16} className="mr-1" />
                      {deleteClient.isPending ? "Deleting..." : "Delete"}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
